import { Home } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { VfsFile } from '../code/codeApi';
import { readBotConfig } from './botConfig';
import {
  buildSitePreview,
  isConsoleMessage,
  isSiteNavigateMessage,
  resolveErrorLoc,
  SITE_HOME_PAGE,
  type ConsoleLine,
  type SiteChatResult,
} from './buildSitePreview';
import {
  ChatbotTranscript,
  createMemoryTranscriptStore,
  createStudioTranscriptStore,
} from './chatbotTranscript';
import {
  fetchProjectSource,
  fetchProjectSourceUrl,
  getChatbotPrice,
  querySiteDb,
  sendChatbotReply,
} from './panes/playgroundApi';
import { useSiteBackendChannel, type SiteBackendTransport } from './siteBackendChannel';
import { createSiteChatTransport, type SiteChatError } from './siteChatTransport';
import type { StudioKind } from './studioKind';
import {
  createRunCollector,
  isAssetMessage,
  isSiteReportMessage,
  PROBE_REPLY_TIMEOUT_MS,
  RUN_OBSERVE_MS,
  type RunReport,
} from './runReport';

interface SiteFrameProps {
  files: VfsFile[];
  /** The backend project whose server-side db answers the frame's `db.query`
   *  sql requests (D-WEB-15). Absent only in a project-less session, where
   *  queries fail kid-readably instead of silently hanging. */
  projectId?: string;
  /** Class shared assets resolved to ready `data:` URLs — inlined like VFS
   *  assets (same seam as GameFrame's virtualAssets). */
  virtualAssets?: VfsFile[];
  /** Bump to force a fresh page load: back to index.html, console cleared.
   *  The db is server-side (D-WEB-15) and simply PERSISTS across reloads —
   *  only the explicit Reset database affordance rebuilds it. */
  runKey: number;
  /** Show the captured console panel under the site (same as GameFrame). */
  showConsole?: boolean;
  /** Called when the kid clicks "Fix this error" on a console error line. */
  onFixError?: (message: string) => void;
  /** Reports the captured console line count whenever it changes. */
  onConsoleCount?: (n: number) => void;
  /** Reports the full captured console lines (so a parent pane can render its
   *  own console panel instead of the built-in one). */
  onConsole?: (lines: ConsoleLine[]) => void;
  /**
   * Post-apply verification (D-WEB-13, same seam as GameFrame): when set, each
   * RUN (runKey) is observed for RUN_OBSERVE_MS, the in-frame shim is asked to
   * report, and the finalized website RunReport (console evidence + the shim's
   * site ledgers) is emitted EXACTLY ONCE per run — with
   * `probeError: 'no-response'` if the shim never answers.
   */
  onRunReport?: (report: RunReport) => void;
  /** 1-based chain attempt stamped into the emitted RunReport (default 1). */
  reportAttempt?: number;
  /**
   * Project kind (creative-code-studio-chatbot-prd D-CB-06): `chatbot` injects
   * the `window.ai` bridge and answers it through the authed per-project reply
   * endpoint (the kid's wallet). `website` (default) renders byte-identically
   * to before — no bridge, no chat.
   */
  kind?: StudioKind;
  /** Read-only host (teacher live view / parent read-only studio, D-CB-11):
   *  `ai.chat` answers VIEW_ONLY locally — no request, no charge. */
  readOnly?: boolean;
  /** A chatbot reply was DELIVERED (and charged) — the studio refetches the balance. */
  onChatReply?: (result: SiteChatResult) => void;
  /** A chatbot reply FAILED — `rawCode` is the backend code (e.g.
   *  `WALLET_INSUFFICIENT` → the studio raises the Out-of-Stars sheet). */
  onChatError?: (rawCode: string, mapped: SiteChatError) => void;
}

const LEVEL_COLOR: Record<ConsoleLine['level'], string> = {
  log: 'text-pg-text-dim',
  info: 'text-pg-text-dim',
  warn: 'text-brand-sunshine',
  error: 'text-brand-coral',
};

/**
 * Renders a kid's WEBSITE inside the strict sandbox (Website Studio,
 * creative-code-studio-website-prd). Same security model as GameFrame:
 * opaque-origin iframe, `allow-scripts` ONLY, NO allow-same-origin; the only
 * channel back to the app is postMessage (console capture + the nav shim +
 * the sql channel).
 *
 * The db is REAL and server-side (D-WEB-15): this component is the frame's
 * token-holding proxy — each in-frame `db.query` arrives as an
 * `action:'sql'` postMessage, is forwarded to `POST /projects/:id/db/query`
 * with the kid's session, and the reply is posted back by request id. The
 * same proxy answers `action:'source'` (D-WEB-19): `sources.get` forwards to
 * `POST /projects/:id/sources/:name` so curated EXTERNAL data reaches the
 * site while the sandbox keeps zero egress. Page
 * navigation carries nothing (state lives on the server); the slim top bar
 * is Home (`site-nav-home`) + the current page (`site-nav-page`).
 */
export function SiteFrame({
  files,
  projectId,
  virtualAssets,
  runKey,
  showConsole = false,
  onFixError,
  onConsoleCount,
  onConsole,
  onRunReport,
  reportAttempt = 1,
  kind = 'website',
  readOnly = false,
  onChatReply,
  onChatError,
}: SiteFrameProps) {
  const [lines, setLines] = useState<ConsoleLine[]>([]);
  const [{ path: page, revision: pageRevision }, setNavigation] = useState({
    path: SITE_HOME_PAGE,
    revision: 0,
  });
  const isChatbot = kind === 'chatbot';

  // ── Chatbot Studio (D-CB-06/07): the bot brain + the device-local transcript ──
  // The price the page shows (`ai.price()`) — the server's current Stars per
  // reply for THIS project (0 under a workshop-free / prep waiver). A plain
  // effect (not a query hook) so the frame needs no QueryClient — it is
  // mounted by hosts and tests that have none. Defaults to the standard 1★
  // until the server answers; a failed lookup keeps the default.
  const [price, setPrice] = useState(1);
  useEffect(() => {
    if (!isChatbot || !projectId) return undefined;
    let alive = true;
    void getChatbotPrice(projectId)
      .then((p) => {
        if (alive && Number.isFinite(p.stars)) setPrice(p.stars);
      })
      .catch(() => {
        // keep the default price
      });
    return () => {
      alive = false;
    };
  }, [isChatbot, projectId]);
  const botConfig = useMemo(() => readBotConfig(files), [files]);
  // ONE transcript per project (IndexedDB-backed; memory for a project-less
  // session). Loaded once; `transcriptReady` flips so the first build after
  // load seeds the page's `ai.history()` mirror with the real conversation.
  const transcript = useMemo(
    () =>
      new ChatbotTranscript(
        projectId ? createStudioTranscriptStore(projectId) : createMemoryTranscriptStore(),
      ),
    [projectId],
  );
  const [transcriptReady, setTranscriptReady] = useState(false);
  useEffect(() => {
    if (!isChatbot) return undefined;
    let alive = true;
    setTranscriptReady(false);
    void transcript.init().then(() => {
      if (alive) setTranscriptReady(true);
    });
    return () => {
      alive = false;
    };
  }, [isChatbot, transcript]);
  // Latest callbacks in refs so the transport never re-creates on a parent render.
  const onChatReplyRef = useRef(onChatReply);
  onChatReplyRef.current = onChatReply;
  const onChatErrorRef = useRef(onChatError);
  onChatErrorRef.current = onChatError;

  // A reload (runKey bump) is a fresh page load: back to the home page,
  // console cleared. The server-side db persists — nothing else resets.
  useEffect(() => {
    setNavigation((current) => ({ path: SITE_HOME_PAGE, revision: current.revision }));
    setLines([]);
  }, [runKey]);

  const { srcDoc, scriptRanges, currentPage } = useMemo(
    () =>
      buildSitePreview(files, {
        page,
        virtualAssets,
        // The transcript mirror is read at BUILD time (never a dep): a reply
        // updates the in-frame mirror itself, and a rebuild would reload the page.
        ...(isChatbot
          ? { chat: { config: botConfig, price, history: transcript.snapshot() } }
          : {}),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- transcriptReady re-seeds the mirror once loaded
    [files, page, virtualAssets, isChatbot, botConfig, price, transcript, transcriptReady],
  );
  // Read inside the (stable) message listener without re-subscribing.
  const scriptRangesRef = useRef(scriptRanges);
  scriptRangesRef.current = scriptRanges;
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // The studio backend transport: the site's `db.query` / `sources.get` /
  // `sources.fetch` calls are forwarded to the AUTHED per-project endpoints
  // with the kid's session (the frame stays token-free). The public play host
  // uses the shareId variant — everything else about the proxy is shared
  // (siteBackendChannel).
  const transport = useMemo<SiteBackendTransport>(
    () => ({
      ready: !!projectId,
      query: (sql, params) => querySiteDb(projectId as string, sql, params),
      getSource: (name, params) => fetchProjectSource(projectId as string, name, params),
      openFetch: (url) => fetchProjectSourceUrl(projectId as string, url),
      // Chatbot Studio only: the chat leg. A project-less session has no brain
      // server-side, so the page gets AI_UNAVAILABLE kid-readably (never a hang).
      ...(isChatbot
        ? {
            chat: createSiteChatTransport({
              transcript,
              readOnly,
              send: (text, history) =>
                sendChatbotReply(projectId as string, { text, history, expectedStars: price }),
              onReply: (r) => onChatReplyRef.current?.(r),
              onError: (raw, mapped) => onChatErrorRef.current?.(raw, mapped),
            }),
          }
        : {}),
    }),
    [projectId, isChatbot, transcript, readOnly, price],
  );
  useSiteBackendChannel(iframeRef, transport);

  // ── Run-report collection (D-WEB-13) — mirrors GameFrame's ────────────────
  // One collector per RUN (runKey) — deliberately NOT per srcDoc: a website
  // live-rebuilds and page-navigates without a fresh run, and the evidence from
  // the whole observed window must survive those srcdoc swaps. Latest callback/
  // attempt in refs so a parent re-render never restarts the observation.
  const onRunReportRef = useRef(onRunReport);
  onRunReportRef.current = onRunReport;
  const reportAttemptRef = useRef(reportAttempt);
  reportAttemptRef.current = reportAttempt;
  const collectorRef = useRef<ReturnType<typeof createRunCollector> | null>(null);
  // Finalize-and-emit for the CURRENT run (null once emitted / between runs).
  const emitReportRef = useRef<(() => void) | null>(null);
  const collectReports = !!onRunReport;

  useEffect(() => {
    if (!collectReports) return undefined;
    const collector = createRunCollector({
      engine: 'website',
      attempt: reportAttemptRef.current,
      assetManifest: [],
    });
    collectorRef.current = collector;
    const startedAt = Date.now();
    let emitted = false;
    const emit = () => {
      if (emitted) return; // at most once per run
      emitted = true;
      emitReportRef.current = null;
      onRunReportRef.current?.(collector.finalize(Date.now() - startedAt));
    };
    emitReportRef.current = emit;
    // Observe, then ask the in-frame shim for its evidence ledgers; if it never
    // answers (shim broke / page wedged), finalize as inconclusive.
    let replyTimer: ReturnType<typeof setTimeout> | undefined;
    const probeTimer = setTimeout(() => {
      iframeRef.current?.contentWindow?.postMessage({ __airbotixControl: true, action: 'report' }, '*');
      replyTimer = setTimeout(() => {
        collector.setProbeError('no-response');
        emit();
      }, PROBE_REPLY_TIMEOUT_MS);
    }, RUN_OBSERVE_MS);
    return () => {
      clearTimeout(probeTimer);
      clearTimeout(replyTimer);
      collectorRef.current = null;
      emitReportRef.current = null;
    };
  }, [collectReports, runKey]);

  useEffect(() => {
    // The sql / source proxy lives in useSiteBackendChannel (shared with the
    // public play host). This listener owns the studio-only concerns: console
    // capture (+ the run-report collector), the run-report probe reply, and
    // page navigation.
    const onMessage = (e: MessageEvent) => {
      // Only THIS frame may feed the console / drive navigation (same
      // defence-in-depth as GameFrame — another frame on the page must not
      // steer the site). A null source (jsdom tests) is not the cross-frame case.
      const frameWindow = iframeRef.current?.contentWindow;
      if (e.source != null && frameWindow != null && e.source !== frameWindow) return;
      if (isConsoleMessage(e.data)) {
        // Map srcdoc-relative locations (syntax errors — sourceURL never
        // applied) back to the kid's file:line; runtime locs pass through.
        const loc = resolveErrorLoc(e.data.loc, scriptRangesRef.current);
        // The collector bounds original diagnostics; log/info lines become site.logs — the
        // model's own console.log instrumentation echoed back (D-WEB-13).
        collectorRef.current?.feedConsole({ ...e.data, loc });
        setLines((prev) => [
          ...prev.slice(-49),
          { level: e.data.level, text: e.data.text, loc, stack: e.data.stack, muted: e.data.muted, hint: e.data.hint },
        ]);
        return;
      }
      if (isSiteReportMessage(e.data)) {
        // The shim answered the run probe with its evidence ledgers.
        collectorRef.current?.feedSite(e.data.site);
        emitReportRef.current?.();
        return;
      }
      if (isAssetMessage(e.data)) {
        // The asset-origin shim's run-time outcome: a picture path a db row /
        // api response named that is NOT in the project lands as `missing-ref`.
        collectorRef.current?.feedAsset(e.data);
        return;
      }
      if (isSiteNavigateMessage(e.data)) {
        // Every page-link click rebuilds the document, INCLUDING a link to the
        // current file. Generated sites may have changed that page's DOM in
        // memory (for example a post detail rendered inside index.html), so a
        // same-page "Back to home" must restore the saved VFS document instead
        // of becoming a React no-op. The db is server-side and persists.
        setNavigation((current) => ({ path: e.data.path, revision: current.revision + 1 }));
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Report the captured console (count + full lines) to the parent.
  useEffect(() => {
    onConsoleCount?.(lines.length);
    onConsole?.(lines);
  }, [lines, onConsoleCount, onConsole]);

  const lastError = [...lines].reverse().find((l) => l.level === 'error' && l.text !== 'ready');

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Slim site nav bar: Home + the page on screen. */}
      <div className="flex shrink-0 items-center gap-2 border-b border-pg-border bg-pg-surface-2 px-3 py-1.5">
        <button
          type="button"
          data-testid="site-nav-home"
          aria-label="Home page"
          onClick={() =>
            setNavigation((current) => ({
              path: SITE_HOME_PAGE,
              revision: current.revision + 1,
            }))
          }
          className="flex items-center gap-1.5 rounded-md px-2 py-1 text-[12px] font-bold text-pg-text-dim transition-colors hover:bg-pg-text/10 hover:text-pg-text"
        >
          <Home size={13} aria-hidden /> Home
        </button>
        <span
          data-testid="site-nav-page"
          className="truncate font-mono text-[12px] text-pg-text-muted"
        >
          {currentPage}
        </span>
      </div>

      <div className="min-h-0 flex-1 bg-pg-surface">
        <iframe
          ref={iframeRef}
          key={`${runKey}:${pageRevision}`}
          title={isChatbot ? 'Chatbot' : 'Site'}
          data-site-frame=""
          data-site-kind={kind}
          // Deliberately allow-scripts ONLY — NO allow-same-origin /
          // allow-top-navigation / allow-forms. Never weaken this.
          sandbox="allow-scripts"
          srcDoc={srcDoc}
          className="h-full w-full border-0"
        />
      </div>

      {showConsole && (
        <div className="shrink-0 max-h-40 overflow-y-auto border-t border-pg-border bg-pg-desktop px-4 py-2">
          <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-pg-text-muted mb-1">Console</div>
          {lines.length === 0 ? (
            <div className="text-[12px] text-pg-text-muted font-mono">…</div>
          ) : (
            <ul className="space-y-0.5">
              {lines.map((l, i) => (
                <li key={i} className={`text-[12px] font-mono ${LEVEL_COLOR[l.level]}`}>
                  {l.level === 'error' ? '⛔ ' : l.level === 'warn' ? '⚠ ' : '› '}
                  {l.text}
                </li>
              ))}
            </ul>
          )}
          {lastError && onFixError && (
            <button
              onClick={() => onFixError(lastError.text)}
              className="mt-2 rounded-full bg-wash-coral px-3 py-1 text-[11px] font-bold text-ink hover:bg-brand-coral hover:text-white transition-colors"
            >
              🤖 Fix this error
            </button>
          )}
        </div>
      )}
    </div>
  );
}
